import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import projectsService from '../services/projectsService';
import deliveriesService from '../services/deliveriesService';
import {
  CheckCircle,
  Circle,
  Clock,
  ChevronRight,
  Play,
  ShoppingCart,
  Hammer,
  Wrench,
  Package,
  Truck,
  RotateCcw
} from 'lucide-react';
import toast from 'react-hot-toast';
import DeliveryModal from './DeliveryModal';

const STAGE_CONFIG = {
  PLANNING: { label: 'Planejamento', icon: Clock, color: 'text-gray-600', bg: 'bg-gray-100' },
  PURCHASING: { label: 'Compras', icon: ShoppingCart, color: 'text-amber-600', bg: 'bg-amber-100' },
  PRODUCTION: { label: 'Produção', icon: Hammer, color: 'text-blue-600', bg: 'bg-blue-100' },
  ASSEMBLY: { label: 'Montagem', icon: Wrench, color: 'text-orange-600', bg: 'bg-orange-100' },
  COMPLETED: { label: 'Concluído', icon: Package, color: 'text-green-600', bg: 'bg-green-100' },
  DELIVERED: { label: 'Entregue', icon: Truck, color: 'text-purple-600', bg: 'bg-purple-100' }
};

const STAGES_ORDER = ['PLANNING', 'PURCHASING', 'PRODUCTION', 'ASSEMBLY', 'COMPLETED', 'DELIVERED'];

const ProjectWorkflow = ({ projectId, currentStage = 'PLANNING', onStageChange, readOnly = false }) => {
  const queryClient = useQueryClient();
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedStage, setSelectedStage] = useState(null);
  const [notes, setNotes] = useState('');
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);

  // Buscar dados do workflow
  const { data: stagesData, isLoading } = useQuery({
    queryKey: ['project-stages', projectId],
    queryFn: () => projectsService.getProjectStages(projectId),
    enabled: !!projectId
  });

  // Buscar dados do projeto (para nome do cliente)
  const { data: projectData } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsService.getProjectById(projectId),
    enabled: !!projectId
  });

  // Verificar se entrega já foi registrada
  const { data: deliveryData } = useQuery({
    queryKey: ['delivery', projectId],
    queryFn: () => deliveriesService.getByProject(projectId),
    enabled: !!projectId && (currentStage === 'COMPLETED' || currentStage === 'DELIVERED')
  });

  const hasDelivery = deliveryData?.data?.id;
  const project = projectData?.data;

  // Mutation para atualizar stage
  const updateStageMutation = useMutation({
    mutationFn: ({ toStage, notes }) => projectsService.updateProjectStage(projectId, toStage, notes),
    onSuccess: (res) => {
      toast.success(`Projeto movido para ${res.data.stage_label}`);
      queryClient.invalidateQueries(['project-stages', projectId]);
      queryClient.invalidateQueries(['projects']);
      setShowConfirm(false);
      setNotes('');
      onStageChange?.(res.data.to_stage);
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Erro ao atualizar etapa');
    }
  });

  const timeline = stagesData?.data?.timeline || [];
  const currentIndex = STAGES_ORDER.indexOf(currentStage);

  // Determinar próxima etapa possível
  const nextStage = currentIndex < STAGES_ORDER.length - 1 ? STAGES_ORDER[currentIndex + 1] : null;
  const nextStageConfig = nextStage ? STAGE_CONFIG[nextStage] : null;

  const handleConfirmStage = () => {
    if (selectedStage) {
      updateStageMutation.mutate({ toStage: selectedStage, notes });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <h3 className="text-sm font-semibold text-gray-700 mb-4">Workflow de Execução</h3>

      {/* Timeline */}
      <div className="flex items-center justify-between mb-6">
        {STAGES_ORDER.map((stage, index) => {
          const config = STAGE_CONFIG[stage];
          const Icon = config.icon;
          const isCompleted = index <= currentIndex;
          const isCurrent = stage === currentStage;

          return (
            <React.Fragment key={stage}>
              <div className="flex flex-col items-center">
                <div className={`
                  w-10 h-10 rounded-full flex items-center justify-center
                  ${isCompleted ? config.bg : 'bg-gray-100'}
                  ${isCurrent ? 'ring-2 ring-primary-500 ring-offset-2' : ''}
                `}>
                  {isCompleted ? (
                    <Icon className={`w-5 h-5 ${config.color}`} />
                  ) : (
                    <Circle className="w-5 h-5 text-gray-300" />
                  )}
                </div>
                <span className={`text-xs mt-1 ${isCompleted ? 'text-gray-700' : 'text-gray-400'}`}>
                  {config.label}
                </span>
              </div>

              {index < STAGES_ORDER.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 ${index < currentIndex ? 'bg-primary-500' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Botão de transição */}
      {!readOnly && nextStage && (
        <div className="flex justify-center">
          <button
            onClick={() => {
              setSelectedStage(nextStage);
              setShowConfirm(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <Play className="w-4 h-4" />
            Iniciar {nextStageConfig?.label}
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Botão de Registrar Entrega (quando projeto está em COMPLETED) */}
      {!readOnly && currentStage === 'COMPLETED' && !hasDelivery && (
        <div className="flex justify-center mt-4">
          <button
            onClick={() => setShowDeliveryModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <Truck className="w-4 h-4" />
            Registrar Entrega
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Status atual */}
      <div className="mt-4 text-center">
        <span className="text-sm text-gray-500">
          Etapa atual: <strong className="text-gray-700">{STAGE_CONFIG[currentStage]?.label || currentStage}</strong>
        </span>
      </div>

      {/* Modal de confirmação */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">
              Confirmar transição para {STAGE_CONFIG[selectedStage]?.label}
            </h3>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Observações (opcional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full p-2 border rounded-lg"
                rows={3}
                placeholder="Adicione observações sobre esta transição..."
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmStage}
                disabled={updateStageMutation.isPending}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {updateStageMutation.isPending ? 'Confirmando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Entrega */}
      {showDeliveryModal && (
        <DeliveryModal
          projectId={projectId}
          projectName={project?.project_name}
          clientName={project?.client_name}
          onClose={() => setShowDeliveryModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries(['project-stages', projectId]);
            queryClient.invalidateQueries(['project', projectId]);
            queryClient.invalidateQueries(['delivery', projectId]);
          }}
        />
      )}
    </div>
  );
};

export default ProjectWorkflow;